import CustomModalV2 from "../../../shared/custom-modal-v2/custom-modal-v2";
import {cn} from "../../../../utils/utils";
import Cap17driveSearcher from "../../../cap17drive/cap17driveSearcher/cap17driveSearcher";
import {useMemo, useState} from "react";
import useUserPreferences from "../../../user-app-config/hooks/use-user-preferences";
import {fetchSerieDetails, fetchTmdbMovieImages, fetchTmdbSerieImages} from "../../../cap17drive/api/cap17drive-api";
import {getTmdbImage} from "../../../../utils/consts";
import ItemsHorizontalScroll from "../../../cap17drive/shared/itemsHorizontalScroll/itemsHorizontalScroll";

import './changeBackgroundModal.scss';
import {useSelector} from "react-redux";
import users from "../../../users";

type TmdbImageItem = {
    aspect_ratio: number;
    file_path: string;
    iso_639_1: string | null;
    iso_3166_1: string | null;
    vote_average: number;
    vote_count: number;
    height: number;
    width: number;
}

const ignoreKeys = ['logos', 'posters'];
const itemsPerPage = 12;

const ChangeBackgroundModal = ({open, onClose}: { open: boolean; onClose: () => void }) => {
    const [selectedItem, setSelectedItem] = useState<any>(null);
    const {updatePreference, loadingUpdate} = useUserPreferences();
    const [images, setImages] = useState<Record<string, TmdbImageItem[]>>({});
    const [keys, setKeys] = useState<string[]>([]);
    const [selectedKey, setSelectedKey] = useState<string>(null);
    const darkMode = useSelector(users.selectors.isDarkMode);
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [showFullImage, setShowFullImage] = useState<TmdbImageItem>(null);
    const [selectedSeason, setSelectedSeason] = useState<number | undefined>(undefined);
    const [selectedEpisode, setSelectedEpisode] = useState<number | undefined>(undefined);

    const handleClose = () => {
        if (loadingUpdate || showFullImage) return;
        setSelectedItem(null);
        setSelectedEpisode(undefined);
        setSelectedSeason(undefined);

        onClose();
    }

    const showingItems = useMemo(() => {
        if (!selectedKey || !images[selectedKey]) return [];
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return images[selectedKey].slice(startIndex, endIndex);
    }, [images, selectedKey, page]);

    const handleSetKey = (key: string) => {
        setSelectedKey(key);
        setTotalPages(images[key] ? Math.ceil(images[key].length / itemsPerPage) : 1);
        setPage(1);
    }

    const handleFetchImages = (res) => {
        const keys = Object.keys(res || {});
        const imagesMap = {} as Record<string, TmdbImageItem[]>;
        keys.forEach(key => {
            if (Array.isArray(res[key]) && !ignoreKeys.includes(key) && res[key].length > 0) {
                imagesMap[key] = res[key];
            }
        });
        const imageKeys = Object.keys(imagesMap);
        setKeys(imageKeys);
        const firstKey = imageKeys.length > 0 ? imageKeys[0] : null;
        setSelectedKey(firstKey);
        if (firstKey) {
            setTotalPages(imagesMap[firstKey] ? Math.ceil(imagesMap[firstKey].length / itemsPerPage) : 1);
        }
        setPage(1);
        setImages(imagesMap);
    }

    const handleAddFromSearcher = async (item: any, isMovie: boolean) => {
        const detailedItemPromise = isMovie ? Promise.resolve(item) : fetchSerieDetails(item.id);
        detailedItemPromise.then(itemData => {
            setSelectedItem(itemData);
        });
        setImages({});

        if (isMovie) {
            fetchTmdbMovieImages(item.id).then(handleFetchImages)
        } else {
            fetchTmdbSerieImages(item.id).then(handleFetchImages)
        }

    }

    return (
        <>
            <CustomModalV2
                open={open} onClose={handleClose} title="Cambiar Fondo"
                className={cn("change-background-modal", {"change-background-modal--dark": darkMode})}
                closeOnBackdropClick={true} closeOnEscape={true}
                footerLeftContent={
                    (selectedItem && selectedKey) ? (
                        <div
                            className={cn("change-background-modal__pagination", {"change-background-modal--dark": darkMode})}>
                            <button className="change-background-modal__keys-btn" disabled={page <= 1}
                                    onClick={() => setPage(page - 1)}>
                                <i className="fa-solid fa-angle-left"></i>
                            </button>
                            <button className="change-background-modal__keys-btn" disabled={page >= totalPages}
                                    onClick={() => setPage(page + 1)}>
                                <i className="fa-solid fa-angle-right"></i>
                            </button>
                            {page}/{totalPages}
                        </div>
                    ) : (
                        <div className="text-muted"><small>
                            Busca una película o serie para cambiar el fondo de tu perfil.
                        </small></div>
                    )
                }
                secondaryButton={selectedItem ? {
                    text: "Volver", onClick: () => {
                        setSelectedItem(null)
                    }, variant: 'secondary'
                } : {
                    text: "Cerrar", onClick: onClose, variant: 'secondary'
                }}
                primaryButton={undefined}>
                <div style={{height: 'min(650px, calc(70dvh - 100px))', overflowY: 'auto'}}>
                    {!selectedItem && open && <Cap17driveSearcher
                        onClickItem={({item, isMovie}) => {
                            handleAddFromSearcher(item, isMovie);
                        }}
                        hideTitle={true} navigateToSearch={false} onlyResults={false} onlyInput={false}
                        className={cn("my-profile-collection-details__items-searcher-modal")}/>}
                    <div>
                        <div style={{marginBottom: '10px'}}
                             className="d-flex align-items-center justify-content-between">
                            <div style={{
                                width: '100%',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}>
                                <strong> {selectedItem?.title || selectedItem?.name}</strong>
                            </div>
                            {selectedItem && !selectedItem.isMovie && !!selectedItem.seasons?.length && (
                                <select className="change-background-modal__season-selector" value={selectedSeason}
                                        onChange={(e) => {
                                            const seasonNum = parseInt(e.target.value);
                                            const newSelectedSeason = isNaN(seasonNum) ? undefined : seasonNum;
                                            setSelectedSeason(newSelectedSeason);
                                            const newSelectedEpisode = newSelectedSeason === undefined ? undefined : 1;
                                            setSelectedEpisode(newSelectedEpisode);
                                            fetchTmdbSerieImages(selectedItem.id, newSelectedSeason, newSelectedEpisode).then(handleFetchImages)
                                        }}>
                                    <option value={""}>Todas las temporadas</option>
                                    {selectedItem.seasons?.map((season) => (
                                        <option key={season.season_number} value={season.season_number}>
                                            {season.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        {selectedSeason !== undefined &&
                            <ItemsHorizontalScroll
                                className="change-background-modal__horizontal-scroll-keys mt-3 mb-3">
                                {Array.from({length: selectedItem.seasons[selectedSeason].episode_count}, (_, i) => i + 1)
                                    .map((epNum) => (
                                        <button key={epNum}
                                                style={{
                                                    minWidth: 'auto',
                                                    textWrap: "nowrap"
                                                }}
                                                onClick={() => {
                                                    setSelectedEpisode(epNum)
                                                    fetchTmdbSerieImages(selectedItem.id, selectedSeason, epNum).then(handleFetchImages)
                                                }}
                                                className={cn("change-background-modal__keys-btn", {
                                                    'active': selectedEpisode === epNum
                                                })}>
                                            Episodio {epNum}
                                        </button>
                                    ))}
                            </ItemsHorizontalScroll>}
                        {selectedItem && selectedKey && (<div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                            gap: '8px',
                            paddingBottom: keys.length > 1 ? "120px" : 0,
                            padding: "3px"
                        }}>
                            {showingItems.map((image, index) => (
                                <div className="change-background-modal__grid-item" key={image.file_path} style={{}}
                                     onClick={() => {
                                         updatePreference('profileBackground', image.file_path).then(() => {
                                             handleClose();
                                         });
                                     }}>
                                    <button
                                        className="change-background-modal__keys-btn change-background-modal__expand-btn"
                                        title="Ver imagen completa"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowFullImage(image);
                                        }}>
                                        <i className="fa-solid fa-up-right-and-down-left-from-center"></i>
                                    </button>
                                    <img
                                        src={getTmdbImage(image.file_path, 'w500')}
                                        alt={`Background ${index + 1}`}
                                        style={{width: '100%', height: 'auto', display: 'block'}}
                                    />
                                </div>
                            ))}
                        </div>)}
                    </div>
                </div>
                {selectedItem && keys.length > 1 && (
                    <div style={{
                        position: "absolute",
                        bottom: "20px",
                        left: "20px",
                        right: "20px",
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        padding: "10px",
                        borderRadius: "25px",
                    }}>
                        <ItemsHorizontalScroll className="change-background-modal__horizontal-scroll-keys">
                            {keys.map((key, index) => (
                                <button key={index}
                                        onClick={() => handleSetKey(key)}
                                        className={cn("change-background-modal__keys-btn", {
                                            'active': selectedKey === key
                                        })}>
                                    {key}
                                </button>
                            ))}
                        </ItemsHorizontalScroll>
                    </div>
                )}
            </CustomModalV2>
            <CustomModalV2
                open={!!showFullImage} onClose={() => setShowFullImage(null)} title="Imagen Completa"
                customContent={true} zIndex={13} className="change-background-modal__full-image-container">
                {showFullImage && (
                    <div className="change-background-modal__full-image-body"
                         style={{
                             aspectRatio: showFullImage.aspect_ratio,
                         }}>
                        <button
                            className="change-background-modal__keys-btn change-background-modal__expand-btn change-background-modal__full-image-close-btn "
                            onClick={() => setShowFullImage(null)}>
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                        <img
                            src={getTmdbImage(showFullImage.file_path, 'original')}
                            alt="Full Background"
                            className="change-background-modal__full-image"
                        />
                    </div>
                )}
            </CustomModalV2>
        </>
    );
}

export default ChangeBackgroundModal;
